// Phase 5 multi-monitor: the pop-out video (#/video/:eventId). The console's main canvas for one event in its own
// browser window, without the app chrome, so it can sit full-size on a second monitor. It rides the camera matrix's
// BroadcastChannel (matrixLink.ts): it follows the console player (camera, alarm clip / live / archive, playhead,
// pause) and its buttons drive that player (Live, Sync to alarm, ±10 s, pause). Without a console on the event it keeps
// its own clock from the first alarm, like the matrix.

import { useEffect, useRef, useState } from 'react';
import { api, ApiError, type EventDetail } from '../api.ts';
import { Icon } from '../components/Icon.tsx';
import { CameraFeed } from '../components/VideoPlayer.tsx';
import { Loading } from '../components/ui.tsx';
import { useLoad, useNow } from '../hooks.ts';
import { useSession } from '../session.tsx';
import { useStreamEvent } from '../stream.tsx';
import { Clock, Notice } from './Matrix.tsx';
import { HEARTBEAT_MS, openChannel, type MatrixCmd, type MatrixMsg } from './matrixLink.ts';
import { alarmMs, camShort, isTyping, normalizeEvent, SEV_CLS } from './util.ts';
import { goLive, initialView, PRE_ROLL_MS, seek, selectedAlarm, toIso, togglePause, vtime, type View } from './view.ts';

export function VideoPopout({ eventId }: { eventId: string | null }) {
  const s = useSession();
  const evLoad = useLoad<EventDetail | null>((sig) => (eventId ? api.event(eventId, sig).then(normalizeEvent) : Promise.resolve(null)), [eventId]);
  const ev = evLoad.data && evLoad.data.id === eventId ? evLoad.data : null;
  const reload = evLoad.reload;
  useStreamEvent('event', (sum) => {
    if (sum.id === eventId) reload();
  });

  const [view, setView] = useState<View | null>(null);
  const [linkedAt, setLinkedAt] = useState<number | null>(null);
  const ch = useRef<BroadcastChannel | null>(null);
  useEffect(() => {
    if (!eventId) return;
    const c = openChannel();
    ch.current = c;
    if (!c) return;
    c.onmessage = (e: MessageEvent<MatrixMsg>) => {
      const m = e.data;
      if (!m || typeof m !== 'object' || m.eventId !== eventId) return;
      if (m.t === 'view') {
        setView({ ...m.view, suspended: false });
        setLinkedAt(Date.now());
      } else if (m.t === 'bye') setLinkedAt(null);
      else if (m.t === 'event') reload();
    };
    c.postMessage({ t: 'hello', eventId } satisfies MatrixMsg);
    return () => {
      c.close();
      ch.current = null;
    };
  }, [eventId, reload]);
  const now = useNow(1000);
  const linked = linkedAt !== null && now - linkedAt < HEARTBEAT_MS * 2.5;
  useEffect(() => {
    if (!ev || view) return;
    const t = setTimeout(() => setView((v) => v ?? initialView(ev)), 700);
    return () => clearTimeout(t);
  }, [ev, view]);

  const act = (cmd: MatrixCmd) => {
    if (!eventId) return;
    if (linked) {
      try {
        ch.current?.postMessage({ t: 'cmd', eventId, ...cmd } satisfies MatrixMsg);
      } catch {
        /* closed */
      }
      return;
    }
    if (!ev) return;
    const t = Date.now();
    setView((v) => {
      if (!v) return v;
      if (cmd.cmd === 'live') return goLive(v, t);
      if (cmd.cmd === 'seek') return seek(v, cmd.at, t, 'archive');
      if (cmd.cmd === 'pause') return (v.pauseStart !== null) === cmd.paused ? v : togglePause(v, t);
      if (cmd.cmd === 'camera') return { ...v, cameraId: cmd.cameraId };
      const a = selectedAlarm(ev, v);
      return a ? { ...seek(v, alarmMs(a) - PRE_ROLL_MS, t, 'archive'), synced: true } : v;
    });
  };
  const actRef = useRef(act);
  actRef.current = act;
  const viewRef = useRef(view);
  viewRef.current = view;
  useEffect(() => {
    const on = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey || e.altKey || e.defaultPrevented || isTyping(e.target)) return;
      const k = e.key.toLowerCase();
      const v = viewRef.current;
      if (k === 'l') actRef.current({ cmd: 'live' });
      else if (k === 's') actRef.current({ cmd: 'sync' });
      else if (k === ' ' && v) {
        e.preventDefault();
        actRef.current({ cmd: 'pause', paused: v.pauseStart === null });
      }
    };
    document.addEventListener('keydown', on);
    return () => document.removeEventListener('keydown', on);
  }, []);
  useEffect(() => {
    document.title = ev ? `Video · ${ev.site.name} · ${ev.displayNo} · EyeQ SOC` : 'Video · EyeQ SOC';
  }, [ev]);

  if (!eventId) return <Notice title="No event" body="Open the pop-out video from an event in the console (Pop out)." />;
  if (evLoad.error && !ev) {
    const st = evLoad.error instanceof ApiError ? evLoad.error.status : 0;
    if (st === 404) return <Notice title="Event not found" body={`There is no event ${eventId}.`} />;
    if (st === 403) return <Notice title="Not available for your role" body="You cannot open this event, so you cannot see its video." />;
    return <Notice title="Could not load the event" error={evLoad.error} onRetry={reload} />;
  }
  if (!ev || !view) {
    return (
      <div className="matrix">
        <div className="mempty">
          <Loading label={ev ? 'Waiting for the console…' : 'Loading video…'} />
        </div>
      </div>
    );
  }
  const tz = ev.siteTimeZone;
  const cam = ev.cameras.find((c) => c.id === view.cameraId) ?? ev.cameras[0] ?? null;
  const canAct = ev.state === 'active' && ev.owner?.id === s.me.userId;
  const paused = view.pauseStart !== null;
  const badge =
    view.mode === 'live' ? ({ text: 'LIVE', cls: 'live' } as const) : view.mode === 'alarm' ? ({ text: 'ALARM CLIP', cls: 'alarm' } as const) : ({ text: view.synced ? 'SYNC' : 'ARCHIVE', cls: 'archive' } as const);
  const closed = ev.state === 'closed' || ev.state === 'dispositioned';
  return (
    <div className="matrix vpop">
      <header className={`mbar s-${SEV_CLS[ev.severity]}`}>
        <span className="mtitle">
          <Icon name="single" />
          Video
        </span>
        <span className="msite">
          <b>{ev.site.name}</b>
          <span className="mono dim">{ev.displayNo}</span>
          <b className="mono">{camShort(cam?.name)}</b>
          <span className="dim">{cam?.name.split(' · ').slice(1).join(' · ')}</span>
        </span>
        <span className="sp" />
        <span className={`mlink${linked ? ' on' : ''}`} title={linked ? 'Following the console player for this event' : 'No console is showing this event: this window keeps its own clock'}>
          <i aria-hidden="true" />
          {linked ? 'Following the console' : 'Not linked to the console'}
        </span>
        <Clock view={view} tz={tz} linked={linked} />
        <div className="mctl" role="group" aria-label="Video time">
          <button type="button" className={`btn sm${view.mode === 'live' ? ' on' : ''}`} onClick={() => act({ cmd: 'live' })} aria-pressed={view.mode === 'live'}>
            Live <kbd>L</kbd>
          </button>
          <button type="button" className={`btn sm${view.synced ? ' on' : ''}`} onClick={() => act({ cmd: 'sync' })} title="The selected alarm's time">
            <Icon name="sync" />
            Sync to alarm <kbd>S</kbd>
          </button>
          <button type="button" className="btn sm icon" onClick={() => act({ cmd: 'seek', at: vtime(view, Date.now()) - 10_000 })} aria-label="Back 10 seconds" title="Back 10 s">
            <Icon name="rew" />
          </button>
          <button type="button" className="btn sm icon" onClick={() => act({ cmd: 'pause', paused: !paused })} aria-label={paused ? 'Play' : 'Pause'} title="Play / pause (Space)">
            <Icon name={paused ? 'play' : 'pause'} />
          </button>
          <button type="button" className="btn sm icon" onClick={() => act({ cmd: 'seek', at: vtime(view, Date.now()) + 10_000 })} disabled={view.mode === 'live'} aria-label="Forward 10 seconds" title="Forward 10 s">
            <Icon name="fwd" />
          </button>
        </div>
      </header>
      {closed && (
        <div className="robar mnotice" role="status">
          <Icon name="lock" />
          <span>This event is closed. The video stays open for review.</span>
        </div>
      )}
      <div className="vpop-stage">
        {cam ? (
          <CameraFeed
            cameraId={cam.id}
            cameraName={cam.name}
            at={toIso(view.seekAt)}
            eventId={canAct ? ev.id : null}
            alarmClip={view.mode === 'alarm'}
            paused={paused}
            badge={badge}
            offline={cam.health?.status === 'offline'}
            nonce={view.nonce}
            epoch={view.epoch ?? 0}
          />
        ) : (
          <div className="mempty">
            <span className="note">This site has no cameras in the replica.</span>
          </div>
        )}
      </div>
      {view.layout === 'quad' && linked && <div className="note mhint">The console shows a quad; this window shows its main camera. Every site camera is in the camera matrix (M).</div>}
    </div>
  );
}
